import React, { useState } from 'react';
import { Button, useEmbedded } from '../../../ui/v2';
import { Callout, EmptyState, Reason } from '../../../ui/v2';
import {
  Checkbox,
  Field,
  Input,
  Listbox,
  type ListboxOption,
  PhoneInput,
} from '../../../ui/v2';
import { ConfirmDialog, Dialog, MenuItem, RowMenu } from '../../../ui/v2';
import { StatusLabel, Tag } from '../../../ui/v2';
import {
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeletonRows,
} from '../../../ui/v2';
import { IconMail, IconMessage, IconPlus } from '../../../ui/v2';
import {
  combineQueries,
  useNotificationActions,
  useNotificationsQuery,
  useStoreInfoQuery,
} from './data';
import {
  type Channel,
  type EventTypeOption,
  type NotificationTarget,
  type SubscriptionRow,
  ALL_EVENTS,
  CHANNEL_LABEL,
  NEW_CONTACT,
  STAFF_MESSAGE,
  contactLabel,
  contactValue,
  groupSubscriptions,
  targetIds,
  unavailableEventReason,
} from './model';
import {
  QueryGate,
  SavedNote,
  SectionBody,
  SectionHead,
  TwoLine,
  useSavedAt,
} from './shared';

/**
 * Notifications — who gets an email or text when something happens.
 *
 * One row per contact, listing the events they're subscribed to. Add/Edit
 * share a dialog: pick an existing contact or create one, tick events.
 * "All events" is exclusive. Events the store can't produce (events or
 * catering off) are disabled with the reason beside them.
 *
 * Voicemail rules in store mode always apply to all voicemails, as in
 * production; category routing stays on the admin Notifications page.
 *
 * Proposal (labelled in the spec): Remove asks for confirmation. Production
 * removes immediately; the subscription can only be rebuilt by hand.
 */

interface EditorState {
  mode: 'create' | 'edit';
  contactChoice: string; // target id, NEW_CONTACT, or ''
  label: string;
  channel: Channel;
  email: string;
  phone: string;
  eventTypes: string[];
}

const emptyEditor = (): EditorState => ({
  mode: 'create',
  contactChoice: '',
  label: '',
  channel: 'email',
  email: '',
  phone: '',
  eventTypes: [],
});

const NotificationsSection: React.FC = () => {
  const info = useStoreInfoQuery();
  const storeId = info.data?.store_id;
  const notifications = useNotificationsQuery(storeId);
  const notificationActions = useNotificationActions(storeId);
  const q = combineQueries(
    [info, notifications],
    () => {
      const store = info.data!;
      const { targets, rules, eventTypes } = notifications.data!;
      return {
        storeId: store.store_id,
        takesEvents: !!store.takes_events,
        takesCatering: !!store.takes_catering,
        targets,
        rules,
        eventTypes,
        rows: groupSubscriptions(rules, targets, eventTypes),
      };
    },
    'Notifications could not be loaded.',
  );
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [removing, setRemoving] = useState<SubscriptionRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [savedAt, markSaved] = useSavedAt();
  const [savedText, setSavedText] = useState('Saved.');
  const embedded = useEmbedded();

  const openAdd = () => {
    setEditor(emptyEditor());
    setError(null);
  };
  const openEdit = (row: SubscriptionRow) => {
    setEditor({
      ...emptyEditor(),
      mode: 'edit',
      contactChoice: String(row.target.id),
      label: row.target.label,
      channel: row.target.channel,
      eventTypes: Array.from(new Set(row.events.map((e) => e.rule.event_type))),
    });
    setError(null);
  };

  const save = async () => {
    if (!editor || !q.data) return;
    const { storeId, rules } = q.data;
    if (editor.eventTypes.length === 0)
      return setError('Select at least one event.');
    const creating =
      editor.mode === 'create' && editor.contactChoice === NEW_CONTACT;
    if (editor.mode === 'create' && !editor.contactChoice)
      return setError('Choose a contact or add a new one.');
    if (creating && editor.channel === 'email' && !editor.email.trim())
      return setError('Email is required.');
    if (creating && editor.channel === 'sms' && !editor.phone.trim())
      return setError(
        'Phone is required. Use a country code, for example +15551234567.',
      );
    setBusy(true);
    setError(null);
    try {
      if (editor.mode === 'create') {
        await notificationActions.createRules({
          store: storeId,
          event_types: editor.eventTypes,
          is_enabled: true,
          target_ids: creating ? undefined : [Number(editor.contactChoice)],
          contact: creating
            ? {
                channel: editor.channel,
                label: editor.label.trim(),
                email: editor.channel === 'email' ? editor.email.trim() : null,
                phone_e164:
                  editor.channel === 'sms' ? editor.phone.trim() : null,
                is_enabled: true,
              }
            : undefined,
          applies_to_all_voicemails: editor.eventTypes.includes(STAFF_MESSAGE)
            ? true
            : undefined,
        });
        setSavedText('Notification saved.');
      } else {
        const targetId = Number(editor.contactChoice);
        const current = rules.filter((r) => targetIds(r).includes(targetId));
        const toRemove = current.filter(
          (r) => !editor.eventTypes.includes(r.event_type),
        );
        const toAdd = editor.eventTypes.filter(
          (v) => !current.some((r) => r.event_type === v),
        );
        for (const rule of toRemove)
          await notificationActions.unlinkTarget(rule, targetId);
        if (toAdd.length) {
          await notificationActions.createRules({
            store: storeId,
            event_types: toAdd,
            is_enabled: true,
            target_ids: [targetId],
            applies_to_all_voicemails: toAdd.includes(STAFF_MESSAGE)
              ? true
              : undefined,
          });
        }
        setSavedText('Notification updated.');
      }
      setEditor(null);
      markSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to save the notification.',
      );
    } finally {
      setBusy(false);
      void notificationActions.refresh();
    }
  };

  const toggleEnabled = async (row: SubscriptionRow) => {
    setRowError(null);
    try {
      await notificationActions.patchTarget(row.target.id, {
        is_enabled: !row.target.is_enabled,
      });
      await notificationActions.refresh();
      setSavedText(
        row.target.is_enabled ? 'Contact disabled.' : 'Contact enabled.',
      );
      markSaved();
    } catch (e) {
      setRowError(
        e instanceof Error ? e.message : 'Failed to update the contact.',
      );
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    setError(null);
    try {
      for (const ev of removing.events)
        await notificationActions.unlinkTarget(ev.rule, removing.target.id);
      setRemoving(null);
      setSavedText('Notification removed.');
      markSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to remove the notification.',
      );
    } finally {
      setBusy(false);
      void notificationActions.refresh();
    }
  };

  // In Getting started with nobody set up yet, the empty state's button is
  // the step's one action; the header's would be a second copy of it.
  const emptyStep =
    embedded && q.status === 'success' && q.data?.rows.length === 0;
  return (
    <SectionBody measure="notifications">
      <SectionHead
        title="Notifications"
        description="Who gets an email or text when something happens — an event request, a voicemail, a cancelled reservation."
        action={
          <>
            <SavedNote when={savedAt}>{savedText}</SavedNote>
            {!emptyStep && (
              <Button
                size="sm"
                variant="primary"
                leading={<IconPlus size={14} />}
                onClick={openAdd}
                disabled={q.status !== 'success'}
              >
                Add notification
              </Button>
            )}
          </>
        }
      />
      {rowError && (
        <Callout tone="danger" className="mt-4">
          {rowError}
        </Callout>
      )}
      <div className={embedded ? 'mt-4' : 'mt-6'}>
        <QueryGate
          query={q}
          skeleton={
            <Table fixed>
              <tbody>
                <TableSkeletonRows rows={4} cols={5} />
              </tbody>
            </Table>
          }
        >
          {({ rows }) =>
            rows.length === 0 ? (
              <EmptyState
                compact={embedded}
                icon={<IconMail />}
                title="No notifications yet"
                // Embedded, the step's intro has just said what this is for.
                description={
                  embedded
                    ? undefined
                    : 'Nobody is told when something happens. Add a contact and pick the events they should hear about.'
                }
                action={
                  <Button
                    size="sm"
                    variant={embedded ? 'primary' : 'secondary'}
                    leading={embedded ? <IconPlus size={14} /> : undefined}
                    onClick={openAdd}
                  >
                    Add notification
                  </Button>
                }
              />
            ) : (
              <Table
                fixed
                className="-mx-4 w-[calc(100%+32px)] lg:-mx-6 lg:w-[calc(100%+48px)]"
              >
                <THead>
                  <tr>
                    <TH width="30%">Contact</TH>
                    <TH width={88}>Channel</TH>
                    <TH>Events</TH>
                    <TH width={112}>Status</TH>
                    <TH width={56} align="right">
                      <span className="sr-only">Actions</span>
                    </TH>
                  </tr>
                </THead>
                <TBody>
                  {rows.map((row) => (
                    <TR
                      key={row.target.id}
                      onActivate={() => openEdit(row)}
                      className={
                        row.target.is_enabled ? undefined : 'text-v2-fg-3'
                      }
                    >
                      <TD truncate className="!h-11">
                        <TwoLine
                          primary={row.target.label || 'Untitled'}
                          secondary={contactValue(row.target)}
                        />
                      </TD>
                      <TD>
                        <Tag>{CHANNEL_LABEL[row.target.channel]}</Tag>
                      </TD>
                      <TD
                        truncate
                        title={row.events.map((e) => e.label).join(', ')}
                      >
                        {row.events.map((e) => e.label).join(' · ')}
                      </TD>
                      <TD>
                        <StatusLabel
                          size="sm"
                          tone={row.target.is_enabled ? 'success' : 'neutral'}
                          hollow={!row.target.is_enabled}
                        >
                          {row.target.is_enabled ? 'Enabled' : 'Disabled'}
                        </StatusLabel>
                      </TD>
                      <TD align="right">
                        <RowMenu
                          label={`Actions for ${row.target.label || contactValue(row.target)}`}
                          onEdit={() => openEdit(row)}
                          onDelete={() => setRemoving(row)}
                          deleteLabel="Remove…"
                          extra={(close) => (
                            <MenuItem
                              onClick={() => {
                                close();
                                void toggleEnabled(row);
                              }}
                              description={
                                row.target.is_enabled
                                  ? undefined
                                  : 'Starts sending again.'
                              }
                            >
                              {row.target.is_enabled ? 'Disable' : 'Enable'}
                            </MenuItem>
                          )}
                        />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )
          }
        </QueryGate>
      </div>

      <NotificationDialog
        editor={editor}
        targets={q.data?.targets ?? []}
        eventTypes={q.data?.eventTypes ?? []}
        takesEvents={q.data?.takesEvents ?? true}
        takesCatering={q.data?.takesCatering ?? true}
        busy={busy}
        error={error}
        onChange={(e) => {
          setEditor(e);
          setError(null);
        }}
        onClose={() => !busy && setEditor(null)}
        onSave={save}
      />

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        title="Remove this notification?"
        confirmLabel="Remove"
        busy={busy}
        consequence={
          removing
            ? `${removing.target.label || contactValue(removing.target)} stops hearing about ${removing.events.length === 1 ? removing.events[0].label.toLowerCase() : `${removing.events.length} events`}. The contact stays on file; the subscriptions can't be restored automatically.`
            : ''
        }
      >
        {error && <Callout tone="danger">{error}</Callout>}
      </ConfirmDialog>
    </SectionBody>
  );
};

const CHANNEL_OPTIONS: ListboxOption<Channel>[] = [
  { value: 'email', label: 'Email', leading: <IconMail size={14} /> },
  { value: 'sms', label: 'Text message', leading: <IconMessage size={14} /> },
];

const NotificationDialog: React.FC<{
  editor: EditorState | null;
  targets: NotificationTarget[];
  eventTypes: EventTypeOption[];
  takesEvents: boolean;
  takesCatering: boolean;
  busy: boolean;
  error: string | null;
  onChange: (e: EditorState) => void;
  onClose: () => void;
  onSave: () => void;
}> = ({
  editor,
  targets,
  eventTypes,
  takesEvents,
  takesCatering,
  busy,
  error,
  onChange,
  onClose,
  onSave,
}) => {
  const isEdit = editor?.mode === 'edit';
  const set = (patch: Partial<EditorState>) =>
    editor && onChange({ ...editor, ...patch });
  const target = isEdit
    ? targets.find((t) => String(t.id) === editor?.contactChoice)
    : undefined;
  const creating = !isEdit && editor?.contactChoice === NEW_CONTACT;

  const toggleEvent = (value: string) => {
    if (!editor) return;
    const selected = editor.eventTypes.includes(value);
    let next: string[];
    if (value === ALL_EVENTS) next = selected ? [] : [ALL_EVENTS];
    else if (selected) next = editor.eventTypes.filter((v) => v !== value);
    else next = [...editor.eventTypes.filter((v) => v !== ALL_EVENTS), value];
    set({ eventTypes: next });
  };
  const allSelected = editor?.eventTypes.includes(ALL_EVENTS) ?? false;

  const contactOptions: ListboxOption[] = [
    ...targets.map((t) => ({
      value: String(t.id),
      label: t.label?.trim() || 'Untitled',
      description: t.is_enabled
        ? contactValue(t)
        : `${contactValue(t)} · Disabled`,
      leading:
        t.channel === 'sms' ? (
          <IconMessage size={14} />
        ) : (
          <IconMail size={14} />
        ),
      text: contactLabel(t),
    })),
    {
      value: NEW_CONTACT,
      label: 'Add a new contact…',
      leading: <IconPlus size={14} />,
      separatorBefore: true,
    },
  ];

  return (
    <Dialog
      open={editor !== null}
      onClose={onClose}
      title={isEdit ? 'Edit notification' : 'Add notification'}
      description={
        isEdit
          ? 'Change which events this contact hears about.'
          : 'Choose who to tell and which events they should hear about.'
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} loading={busy}>
            {isEdit ? 'Save' : 'Add notification'}
          </Button>
        </>
      }
    >
      {editor && (
        <div className="flex flex-col gap-4">
          {isEdit && target ? (
            <div className="flex items-center justify-between gap-3 border-b border-v2-line pb-3">
              <TwoLine
                primary={target.label || 'Untitled'}
                secondary={contactValue(target)}
              />
              <Tag>{CHANNEL_LABEL[target.channel]}</Tag>
            </div>
          ) : (
            <Field label="Contact">
              {(id) => (
                <Listbox
                  id={id}
                  value={editor.contactChoice}
                  onChange={(contactChoice) => set({ contactChoice })}
                  options={contactOptions}
                  placeholder="Select a contact"
                  data-autofocus="true"
                />
              )}
            </Field>
          )}

          {creating && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-l-2 border-v2-line pl-4">
              <Field label="Name" hint="Who this is, e.g. Manager or Kitchen.">
                {(id) => (
                  <Input
                    id={id}
                    placeholder="Manager"
                    value={editor.label}
                    onChange={(e) => set({ label: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Channel">
                {(id) => (
                  <Listbox<Channel>
                    id={id}
                    value={editor.channel}
                    onChange={(channel) => set({ channel })}
                    options={CHANNEL_OPTIONS}
                  />
                )}
              </Field>
              {editor.channel === 'email' ? (
                <Field label="Email" className="col-span-2">
                  {(id) => (
                    <Input
                      id={id}
                      type="email"
                      placeholder="manager@example.com"
                      value={editor.email}
                      invalid={!!error && !editor.email.trim()}
                      onChange={(e) => set({ email: e.target.value })}
                    />
                  )}
                </Field>
              ) : (
                <Field label="Phone" className="col-span-2">
                  {(id) => (
                    <PhoneInput
                      id={id}
                      value={editor.phone}
                      invalid={!!error && !editor.phone.trim()}
                      onChange={(phone) => set({ phone })}
                    />
                  )}
                </Field>
              )}
            </div>
          )}

          <fieldset>
            <legend className="text-v2-12 font-medium text-v2-fg-2">
              Events
            </legend>
            <div className="mt-1.5 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              {eventTypes.map((opt) => {
                const unavailable = unavailableEventReason(
                  opt.value,
                  takesEvents,
                  takesCatering,
                );
                const checked = editor.eventTypes.includes(opt.value);
                const dimmed = allSelected && opt.value !== ALL_EVENTS;
                return (
                  <div key={opt.value} className="min-w-0">
                    <Checkbox
                      label={
                        opt.value === STAFF_MESSAGE ? 'Voicemails' : opt.label
                      }
                      checked={checked || dimmed}
                      disabled={!!unavailable || dimmed}
                      onChange={() => toggleEvent(opt.value)}
                      className={
                        unavailable || dimmed ? 'text-v2-fg-3' : undefined
                      }
                    />
                    {unavailable && (
                      <Reason className="ml-6">{unavailable}</Reason>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>

          {error && <Callout tone="danger">{error}</Callout>}
        </div>
      )}
    </Dialog>
  );
};

export default NotificationsSection;
